import { useState, useEffect, useCallback } from 'react';
import { Check, X, ChevronDown, ChevronRight, Settings2, Zap } from 'lucide-react';
import { salesControllingApi } from '../../services/salesControllingApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { SurchargeProductConfig } from './SurchargeProductConfig';
import type { SCFilters, SurchargeInvoice, InvoiceLine, ExistingAllocation, AllocationEntry } from '../../types/salesControlling';

const fmt = (n: number, decimals = 2) =>
    n.toLocaleString('de-CH', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

interface Props {
    filters: SCFilters;
    canEdit: boolean;
    refreshKey: number;
    documentFilter?: string;
    onClearDocumentFilter?: () => void;
}

export function SurchargeAllocationTab({ filters, canEdit, refreshKey, documentFilter, onClearDocumentFilter }: Props) {
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;
    const { toast } = useToast();
    const [invoices, setInvoices] = useState<SurchargeInvoice[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
    const [showConfig, setShowConfig] = useState(false);

    const loadInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const result = await salesControllingApi.getSurchargeInvoices({
                company: filters.company,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: statusFilter,
                page,
                limit: 25,
            });
            setInvoices(result.data);
            setTotal(result.pagination.total);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load invoices', 'error');
        } finally {
            setLoading(false);
        }
    }, [filters.company, filters.dateFrom, filters.dateTo, statusFilter, page, toast]);

    useEffect(() => {
        loadInvoices();
    }, [loadInvoices, refreshKey]);

    useEffect(() => {
        setPage(1);
    }, [filters.company, filters.dateFrom, filters.dateTo, statusFilter]);

    // Auto-expand when navigating from Price Overview
    useEffect(() => {
        if (documentFilter && invoices.length > 0) {
            const match = invoices.find(inv => inv.documentCode === documentFilter);
            if (match) {
                setExpandedInvoice(`${match.companyCode}-${match.documentCode}`);
            }
        }
    }, [documentFilter, invoices]);

    const handleBulkAutoAllocate = async () => {
        const autoInvoices = invoices.filter(inv => inv.canAutoAllocate && inv.allocationStatus === 'unallocated');
        if (autoInvoices.length === 0) {
            toast('No single-product invoices to auto-allocate', 'info');
            return;
        }

        let successCount = 0;
        for (const inv of autoInvoices) {
            try {
                const details = await salesControllingApi.getInvoiceDetails(inv.documentCode, inv.companyCode);
                const surchargeLines = details.lines.filter(l => l.isSurcharge);
                const productLines = details.lines.filter(l => !l.isSurcharge);

                if (productLines.length !== 1) continue;

                const target = productLines[0];
                const allocations: AllocationEntry[] = surchargeLines.map(sl => ({
                    surchargeLineNr: sl.documentLineNr,
                    surchargeProductID: sl.productID,
                    surchargeAmount: sl.netSalesEUR,
                    targetProductID: target.productID,
                    targetLineNr: target.documentLineNr,
                    allocatedAmount: sl.netSalesEUR,
                    allocationMethod: 'auto_single',
                }));

                await salesControllingApi.saveAllocation({
                    companyCode: inv.companyCode,
                    invoiceDocumentCode: inv.documentCode,
                    allocations,
                });
                successCount++;
            } catch {
                // Continue with next invoice
            }
        }

        toast(`Auto-allocated ${successCount} invoice(s)`, 'success');
        loadInvoices();
    };

    const totalPages = Math.ceil(total / 25);
    const statusBadge = (status: string) => {
        const styles: Record<string, string> = {
            allocated: 'bg-green-100 text-green-700',
            partial: 'bg-yellow-100 text-yellow-700',
            unallocated: 'bg-red-100 text-red-700',
        };
        return (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="space-y-4">
            {/* Document filter badge */}
            {documentFilter && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                    <span className="text-blue-700">Filtered to document: <strong>{documentFilter}</strong></span>
                    <button
                        onClick={onClearDocumentFilter}
                        className="text-blue-500 hover:text-blue-700 text-xs underline"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {/* Controls */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-500">Status:</label>
                        <select
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                            className="border rounded-md px-2 py-1 text-sm bg-white"
                        >
                            <option value="all">All</option>
                            <option value="unallocated">Unallocated</option>
                            <option value="allocated">Allocated</option>
                        </select>
                    </div>
                    <span className="text-sm text-gray-500">{total} invoice(s)</span>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <button
                            onClick={handleBulkAutoAllocate}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            <Zap className="w-3.5 h-3.5" />
                            Auto-Allocate Single Products
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            onClick={() => setShowConfig(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 text-gray-600"
                        >
                            <Settings2 className="w-3.5 h-3.5" />
                            Surcharge Config
                        </button>
                    )}
                </div>
            </div>

            {/* Invoice List */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                            <th className="py-2 pr-2 w-8"></th>
                            <th className="py-2 px-2">Date</th>
                            <th className="py-2 px-2">Document</th>
                            <th className="py-2 px-2">Customer</th>
                            <th className="py-2 px-2 text-right">Surcharges</th>
                            <th className="py-2 px-2 text-right">Products</th>
                            <th className="py-2 px-2 text-right">Total Surcharge</th>
                            <th className="py-2 px-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={8} className="py-12 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                                        <span className="text-sm text-gray-400">Loading invoices...</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && invoices.length === 0 && (
                            <tr>
                                <td colSpan={8} className="py-8 text-center text-gray-400">No invoices with surcharges found</td>
                            </tr>
                        )}
                        {!loading && invoices.map(inv => {
                            const key = `${inv.companyCode}-${inv.documentCode}`;
                            const isExpanded = expandedInvoice === key;
                            return (
                                <InvoiceRow
                                    key={key}
                                    invoice={inv}
                                    isExpanded={isExpanded}
                                    onToggle={() => setExpandedInvoice(isExpanded ? null : key)}
                                    statusBadge={statusBadge}
                                    canEdit={canEdit}
                                    onAllocationSaved={loadInvoices}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Surcharge Product Config Modal */}
            {showConfig && (
                <SurchargeProductConfig onClose={() => setShowConfig(false)} />
            )}
        </div>
    );
}

// --- Invoice Row with expandable detail ---

function InvoiceRow({
    invoice,
    isExpanded,
    onToggle,
    statusBadge,
    canEdit,
    onAllocationSaved,
}: {
    invoice: SurchargeInvoice;
    isExpanded: boolean;
    onToggle: () => void;
    statusBadge: (s: string) => React.ReactNode;
    canEdit: boolean;
    onAllocationSaved: () => void;
}) {
    return (
        <>
            <tr
                className="border-b hover:bg-gray-50 cursor-pointer"
                onClick={onToggle}
            >
                <td className="py-2 pr-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </td>
                <td className="py-2 px-2">{new Date(invoice.postingDate).toLocaleDateString('de-CH')}</td>
                <td className="py-2 px-2 font-mono text-xs">{invoice.documentCode}</td>
                <td className="py-2 px-2">{invoice.customerName}</td>
                <td className="py-2 px-2 text-right">{invoice.surchargeCount}</td>
                <td className="py-2 px-2 text-right">{invoice.productLineCount}</td>
                <td className="py-2 px-2 text-right font-medium">{fmt(invoice.totalSurchargeAmount)}</td>
                <td className="py-2 px-2">{statusBadge(invoice.allocationStatus)}</td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={8} className="bg-gray-50 p-0">
                        <InvoiceDetailPanel
                            invoice={invoice}
                            canEdit={canEdit}
                            onSaved={onAllocationSaved}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}

// --- Invoice Detail Panel with allocation controls ---

function InvoiceDetailPanel({
    invoice,
    canEdit,
    onSaved,
}: {
    invoice: SurchargeInvoice;
    canEdit: boolean;
    onSaved: () => void;
}) {
    const { toast } = useToast();
    const [lines, setLines] = useState<InvoiceLine[]>([]);
    const [existingAllocations, setExistingAllocations] = useState<ExistingAllocation[]>([]);
    const [selectedTargets, setSelectedTargets] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        salesControllingApi.getInvoiceDetails(invoice.documentCode, invoice.companyCode)
            .then(result => {
                setLines(result.lines);
                setExistingAllocations(result.existingAllocations);
                // Pre-select targets from existing allocations
                const preSelected = new Set(result.existingAllocations.map(a => a.targetLineNr));
                if (preSelected.size > 0) {
                    setSelectedTargets(preSelected);
                }
            })
            .catch(err => toast(err.message, 'error'))
            .finally(() => setLoading(false));
    }, [invoice.documentCode, invoice.companyCode, toast]);

    const surchargeLines = lines.filter(l => l.isSurcharge);
    const productLines = lines.filter(l => !l.isSurcharge);

    const toggleTarget = (lineNr: number) => {
        setSelectedTargets(prev => {
            const next = new Set(prev);
            if (next.has(lineNr)) next.delete(lineNr);
            else next.add(lineNr);
            return next;
        });
    };

    const selectAllProducts = () => {
        setSelectedTargets(new Set(productLines.map(l => l.documentLineNr)));
    };

    // Calculate proportional allocation preview
    const calculatePreview = useCallback((): AllocationEntry[] => {
        const selected = productLines.filter(l => selectedTargets.has(l.documentLineNr));
        if (selected.length === 0) return [];

        const totalQty = selected.reduce((sum, l) => sum + Math.abs(l.quantity), 0);
        if (totalQty === 0) return [];

        const allocations: AllocationEntry[] = [];
        const method = selected.length === 1 && productLines.length === 1 ? 'auto_single' : 'manual_proportional';

        for (const surcharge of surchargeLines) {
            let remaining = surcharge.netSalesEUR;
            for (let i = 0; i < selected.length; i++) {
                const target = selected[i];
                const proportion = Math.abs(target.quantity) / totalQty;
                const amount = i === selected.length - 1
                    ? remaining // Last item gets the remainder (rounding fix)
                    : Math.round(surcharge.netSalesEUR * proportion * 100) / 100;
                remaining -= amount;

                allocations.push({
                    surchargeLineNr: surcharge.documentLineNr,
                    surchargeProductID: surcharge.productID,
                    surchargeAmount: surcharge.netSalesEUR,
                    targetProductID: target.productID,
                    targetLineNr: target.documentLineNr,
                    allocatedAmount: amount,
                    allocationMethod: method,
                });
            }
        }

        return allocations;
    }, [surchargeLines, productLines, selectedTargets]);

    const previewAllocations = calculatePreview();

    const handleSave = async () => {
        if (previewAllocations.length === 0) {
            toast('Please select at least one product to allocate to', 'error');
            return;
        }

        setSaving(true);
        try {
            await salesControllingApi.saveAllocation({
                companyCode: invoice.companyCode,
                invoiceDocumentCode: invoice.documentCode,
                allocations: previewAllocations,
            });
            toast('Allocation saved', 'success');
            onSaved();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await salesControllingApi.deleteAllocation(invoice.documentCode, invoice.companyCode);
            toast('Allocation removed', 'success');
            setSelectedTargets(new Set());
            setExistingAllocations([]);
            onSaved();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to delete', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-4 text-center text-gray-400">Loading invoice details...</div>;
    }

    // Build a map of line → allocated amounts from preview
    const allocationByTarget = new Map<number, number>();
    for (const a of previewAllocations) {
        allocationByTarget.set(a.targetLineNr, (allocationByTarget.get(a.targetLineNr) || 0) + a.allocatedAmount);
    }

    return (
        <div className="p-4 space-y-4">
            {/* Surcharge Lines */}
            <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Surcharge Lines</h4>
                <div className="space-y-1">
                    {surchargeLines.map(l => (
                        <div key={l.documentLineNr} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-sm">
                            <div>
                                <span className="font-medium">{l.productName}</span>
                                <span className="text-gray-500 ml-2">({l.surchargeType})</span>
                            </div>
                            <span className="font-semibold">{fmt(l.netSalesEUR)} EUR</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Product Lines with selection */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase">Product Lines — Select allocation targets</h4>
                    {canEdit && productLines.length > 1 && (
                        <button onClick={selectAllProducts} className="text-xs text-blue-600 hover:underline">
                            Select All
                        </button>
                    )}
                </div>
                <div className="space-y-1">
                    {productLines.map(l => {
                        const isSelected = selectedTargets.has(l.documentLineNr);
                        const allocated = allocationByTarget.get(l.documentLineNr);
                        return (
                            <div
                                key={l.documentLineNr}
                                className={`flex items-center justify-between rounded px-3 py-1.5 text-sm border cursor-pointer transition-colors ${
                                    isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                                onClick={() => canEdit && toggleTarget(l.documentLineNr)}
                            >
                                <div className="flex items-center gap-2">
                                    {canEdit && (
                                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                        }`}>
                                            {isSelected && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                    )}
                                    <span className="font-medium">{l.productName}</span>
                                    <span className="text-gray-500">{Math.abs(l.quantity)} {l.salesUnit}</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span>{fmt(l.netSalesEUR)} EUR</span>
                                    {allocated !== undefined && (
                                        <span className="text-blue-600 font-medium">+{fmt(allocated)}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Action buttons */}
            {canEdit && (
                <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                        {existingAllocations.length > 0 && (
                            <button
                                onClick={handleDelete}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                            >
                                <X className="w-3.5 h-3.5" />
                                Remove Allocation
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving || selectedTargets.size === 0}
                        className="flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Check className="w-3.5 h-3.5" />
                        {saving ? 'Saving...' : 'Save Allocation'}
                    </button>
                </div>
            )}
        </div>
    );
}
